import { Badge, Box, Card, HStack, SimpleGrid, Spinner, Stack, Text } from "@chakra-ui/react";
import { MdOutlinePeople } from "react-icons/md";
import { useDashboardCampSocios } from "features/dashboard/camp/socios/hooks";
import AppAvatar from "components/utils/AppAvatar";
import StatCard from "components/utils/StatCard";

const CampSocioCard = ({ socio }) => {
  const name = socio?.socioNombre?.trim() || "Socio sin nombre";
  const generacion = socio?.generacion ?? "—";
  const talla = socio?.talla || "—";
  const colegio = socio?.socioColegio?.trim() || "Sin colegio";
  const seed = socio?.socioId || name;

  return (
    <Card.Root flexDirection="row" overflow="hidden" bg="dashboard.bg.surface" _hover={{ bg: "dashboard.bg.surfaceSubtle" }}>
      <Box p="4" display="flex" alignItems="center" justifyContent="center">
        <AppAvatar name={name} seed={seed} size="lg" />
      </Box>
      <Card.Body>
        <Card.Title mb="1">{name}</Card.Title>
        <Card.Description>
          <Stack gap="1">
            <HStack gap="2" wrap="wrap">
              <Badge>{`Generación ${generacion}`}</Badge>
              <Badge colorPalette="orange">{`Talla ${talla}`}</Badge>
            </HStack>
            <Text color="fg.muted" textStyle="sm">
              {colegio}
            </Text>
          </Stack>
        </Card.Description>
      </Card.Body>
    </Card.Root>
  );
};

const CampSocios = () => {
  const q = useDashboardCampSocios();

  if (q.isError) {
    return (
      <pre style={{ whiteSpace: "pre-wrap", color: "crimson" }}>
        {String(q.error?.message || q.error)}
      </pre>
    );
  }

  if (q.isLoading) {
    return (
      <Box py="10" display="flex" alignItems="center" justifyContent="center">
        <Spinner />
      </Box>
    );
  }

  const groups = Array.isArray(q.data) ? q.data : [];
  const counts = new Map();
  for (const group of groups) {
    const socios = Array.isArray(group?.socios) ? group.socios : [];
    for (const socio of socios) {
      const key = String(socio?.generacion ?? "Sin generación");
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
  }
  const generationStats = Array.from(counts.entries())
    .sort((a, b) => Number(b[0]) - Number(a[0]))
    .map(([generacion, total], index) => ({
      generacion,
      total,
      tone: ["info", "success", "warning", "danger"][index % 4],
    }));

  return (
    <Box>
      <Text fontWeight="semibold" textStyle="4xl">
        Socios
      </Text>
      <Text pb="5">Socios registrados por campamento.</Text>

      {generationStats.length > 0 ? (
        <HStack pb="5" gap="2" wrap="wrap" alignItems="stretch">
          {generationStats.map((stat) => (
            <StatCard
              key={stat.generacion}
              title={`Generación ${stat.generacion}`}
              data={String(stat.total)}
              icon={MdOutlinePeople}
              tone={stat.tone}
            />
          ))}
        </HStack>
      ) : null}

      {groups.length === 0 ? (
        <Text color="fg.muted">No hay socios registrados en campamentos.</Text>
      ) : (
        <Stack gap="5">
          {groups.map((group) => {
            const socios = Array.isArray(group?.socios) ? group.socios : [];
            return (
              <Box key={group.campamentoId} bg="dashboard.bg.surface" borderWidth="1px" borderColor="dashboard.border.default" rounded="xl" p="4">
                <HStack justifyContent="space-between" mb="3">
                  <Text fontWeight="semibold" textStyle="xl">
                    {group.campamentoNombre}
                  </Text>
                  <Badge colorPalette="green">{`${socios.length} socios`}</Badge>
                </HStack>

                <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} gap="2">
                  {socios.map((socio) => (
                    <CampSocioCard key={socio.socioId} socio={socio} />
                  ))}
                </SimpleGrid>
              </Box>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

export default CampSocios;
